<template>
  <div
    class="vehicle-card bg-white dark:bg-slate-800 rounded-xl shadow-xs overflow-hidden"
    :class="{ 'opacity-50': loading }"
  >
    <!-- Lazy loaded image with improved loading experience -->
    <div class="relative aspect-16/9 bg-slate-100 dark:bg-slate-700">
      <!-- Loading placeholder with blur effect -->
      <div
        v-if="!imageLoaded"
        class="absolute inset-0 bg-slate-200 dark:bg-slate-600 animate-pulse flex items-center justify-center"
      >
        <div class="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent"></div>
      </div>

      <!-- Low quality placeholder (blurred) -->
      <img
        v-if="vehicle.thumb || vehicle.image"
        :src="vehicle.thumb || vehicle.image"
        :alt="getVehicleTitle"
        class="absolute inset-0 w-full h-full object-cover transform scale-110 blur-sm opacity-60"
        loading="lazy"
        fetchpriority="low"
      />

      <!-- High quality image -->
      <img
        :src="vehicle.thumb || vehicle.image"
        :alt="getVehicleTitle"
        class="w-full h-full object-cover transition-all duration-500"
        :class="{ 'opacity-0': !imageLoaded }"
        loading="lazy"
        decoding="async"
        fetchpriority="high"
        @load="onImageLoad"
        @error="handleImageError"
      />
    </div>

    <!-- Vehicle Info -->
    <div class="p-4">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-2">
        {{ getVehicleTitle }}
      </h3>

      <!-- Price -->
      <div class="flex items-center gap-2 mb-2">
        <span class="text-xl font-bold text-primary">
          {{ formatPrice(vehicle.price) }}
        </span>
        <span v-if="vehicle.perweek" class="text-sm text-slate-500 dark:text-slate-400">
          or {{ formatPrice(vehicle.perweek) }}/week
        </span>
      </div>

      <!-- Key Details -->
      <div class="grid grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-300 mb-4">
        <div class="flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 7H7v6h6V7z" />
            <path fill-rule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm2 0h10v10H5V5z" clip-rule="evenodd" />
          </svg>
          {{ formatKms(vehicle.kms) }}
        </div>
        <div class="flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
          </svg>
          {{ vehicle.year?.displayValue?.[0] || 'N/A' }}
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          @click="$emit('quick-view', vehicle.id)"
        >
          Quick View
        </Button>
        <Button
          variant="ghost"
          size="sm"
          @click="$emit('favorite', vehicle.id)"
          :class="{ 'text-red-500': isFavorite }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :fill="isFavorite ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { Button } from '~/layers/base/components/ui/button'

const props = defineProps<{
  vehicle: any
}>()

const emit = defineEmits<{
  (e: 'quick-view', id: string): void
  (e: 'favorite', id: string): void
}>()

const vehiclesStore = useVehiclesStore()
const loading = ref(false)
const imageLoaded = ref(false)
const imageError = ref(false)

// Handle image load event
const onImageLoad = () => {
  imageLoaded.value = true;
  loading.value = false;
};

// Check if image is already cached on mount
onMounted(() => {
  // Check if the image might already be in browser cache
  if (props.vehicle.thumb || props.vehicle.image) {
    const img = new Image();
    img.onload = onImageLoad;
    img.onerror = handleImageError;
    img.src = props.vehicle.thumb || props.vehicle.image;

    // If the image is already cached, the onload event might not fire
    // Set a timeout to check if the image is complete
    setTimeout(() => {
      if (img.complete) {
        onImageLoad();
      }
    }, 50);
  }
});

// Computed properties
const getVehicleTitle = computed(() => {
  if (props.vehicle.title) return props.vehicle.title
  if (props.vehicle.make?.displayValue?.[0] && props.vehicle.model?.displayValue?.[0]) {
    return `${props.vehicle.make.displayValue[0]} ${props.vehicle.model.displayValue[0]}`
  }
  return 'Unknown Vehicle'
})

const isFavorite = computed(() => {
  return vehiclesStore.isFavorite(props.vehicle.id)
})

// Helper functions
const formatPrice = (price: number | string) => {
  if (!price) return 'N/A'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(Number(price))
}

const formatKms = (kms: number) => {
  if (!kms) return 'N/A'
  return new Intl.NumberFormat('en-AU').format(kms) + ' km'
}

const handleImageError = () => {
  imageError.value = true
  loading.value = false
}
</script>

<style scoped>
.vehicle-card {
  transition: all 0.3s ease-in-out;
}

.vehicle-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Image loading transitions */
img {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

/* Ensure the aspect ratio container maintains proper dimensions */
.aspect-16\/9 {
  position: relative;
}

.aspect-16\/9::before {
  content: "";
  display: block;
  padding-top: 56.25%; /* 9/16 = 0.5625 = 56.25% */
}

.aspect-16\/9 > * {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Loading animation */
@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .aspect-16\/9::before {
    padding-top: 66.67%; /* 2/3 aspect ratio on mobile for better visibility */
  }
}
</style>